import React from 'react';
import { INormalizedProduct } from '../../models/NormalizedProduct';
import { ProgressIndicator } from '@fluentui/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import CurrencyFormat from 'react-currency-format';
import { useGetVendorSettingsQuery } from '../../store/Api';
import { renderVendorNameFromSettings } from '../../logic/Product';
import VendorPeek from './VendorPeek';

interface IBestPriceDisplayProps {
    /** How an in-stock best price is marked: solid green cell or green text on a plain cell. */
    affirmative?: 'fill' | 'text';
    product: INormalizedProduct;
    refreshingVendors?: string[];
    isExample?: boolean;
}

const BestPriceDisplay: React.FC<IBestPriceDisplayProps> = (props) => {
    const vendorSettings = useGetVendorSettingsQuery(props.product.bestVendor ?? '', {skip: (props.isExample || !props.product.bestVendor)});
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);
    // Green only when the vendor supplying the best price actually has stock; another vendor's stock
    // is not this price's stock.
    const bestVendor = props.product.vendors?.find(v => (v.vendorName ?? '').toLowerCase() === (props.product.bestVendor ?? '').toLowerCase());
    const inStock = bestVendor
        ? (bestVendor.inStock || (bestVendor.onHand ?? 0) > 0)
        : (props.product.bestVendorOnHand ?? 0) > 0;
    // If any vendors still refreshing, show a loading spinner
    if (props.refreshingVendors && props.refreshingVendors.length > 0) {
        return (<div
            className={`vendor-dense text-center ${inStock ? 'vendor-loading-in-stock-cell' : 'vendor-loading-no-stock-cell'})}`}
            >
            <ProgressIndicator label='Loading'/>
        </div>);
    }

    if (props.product.bestPrice == null ||
        props.product.bestBasePrice == null ||
        props.product.bestVendor == null)
        return (<div className='cost-unavailable figure-cell'><span className='figure-main' style={{fontWeight: 400}}>No data</span></div>);

    const discount = props.product.bestBasePrice > props.product.bestPrice
        ? (props.product.bestBasePrice - props.product.bestPrice).toFixed(2)
        : null;


    return (
        <VendorPeek product={props.product} highlight={props.product.bestVendor} isExample={props.isExample}>
        <div 
            className={`vendor-dense figure-cell ${inStock
                ? (props.affirmative === 'text' ? 'in-stock-text' : 'in-stock-dense')
                : (props.affirmative === 'text' ? 'cell-neutral' : 'no-stock-dense')}`}
        >
            <span className='figure-top' title={renderVendorNameFromSettings(props.product.bestVendor, vendorSettings.data)}>
                {renderVendorNameFromSettings(props.product.bestVendor, vendorSettings.data)}
            </span>
            <span className='figure-main'>
                <CurrencyFormat
                    value={props.product.bestPrice}
                    displayType="text"
                    thousandSeparator=","
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale={true}
                />
            </span>
            <span className='figure-bottom'>
                {discount && <span>
                    Discount <CurrencyFormat
                        value={discount}
                        displayType="text"
                        thousandSeparator=","
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                </span>}
                {props.product.bestVendorOnHand != null && props.product.bestVendorOnHand > 0 && desiredQuantity && props.product.bestVendorOnHand < desiredQuantity &&
                    <span style={{color: 'red', display: 'block'}}>Only {props.product.bestVendorOnHand} in stock</span>}
                {props.product.bestVendorOnHand === 0 && <span style={{color: 'red', display: 'block'}}>No Stock</span>}
            </span>
        </div>
        </VendorPeek>
    );
};

export default BestPriceDisplay;